import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaChartLine,
    FaUserGraduate,
    FaSearch,
    FaEye,
    FaRegFileAlt,
    FaDownload,
    FaFilter,
    FaCheckCircle,
    FaExclamationTriangle,
    FaUserClock,
    FaTimes,
    FaChevronRight,
    FaGraduationCap,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaGlobe,
    FaHistory,
    FaTrophy
} from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import EmptyState from '../../shared/EmptyState';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './TutorDashboardOverview.css';

const TutorStudents = () => {
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [performanceData, setPerformanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal States
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [profileDetails, setProfileDetails] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedCourseId) {
            fetchPerformanceMetrics();
        }
    }, [selectedCourseId]);

    const fetchInitialData = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/tutor/courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCourses(res.data);
            if (res.data.length > 0) {
                setSelectedCourseId(res.data[0]._id);
            } else {
                setLoading(false);
            }
        } catch (err) {
            showError('Initialization Error', 'Could not load your assigned courses.');
            setLoading(false);
        }
    };

    const fetchPerformanceMetrics = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/tutor/performance/aggregate?courseId=${selectedCourseId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPerformanceData(res.data);
        } catch (err) {
            showError('Sync Error', 'Failed to aggregate student performance data.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewProfile = async (student) => {
        setSelectedStudent(student);
        setIsProfileModalOpen(true);
        setProfileLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/tutor/student-profile/${student.studentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfileDetails(res.data);
        } catch (err) {
            showError('Profile Error', 'Could not retrieve detailed candidate records.');
        } finally {
            setProfileLoading(false);
        }
    };

    const closeProfileModal = () => {
        setIsProfileModalOpen(false);
        setSelectedStudent(null);
        setProfileDetails(null);
    };

    useEffect(() => {
        if (isProfileModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isProfileModalOpen]);

    const handleExportCSV = () => {
        if (!performanceData || performanceData.studentMetrics.length === 0) {
            showError('Export Failure', 'No performance data exists to export.');
            return;
        }

        showLoading('Generating Professional Performance Report...');
        try {
            const headers = ["Student Name", "Email", "Completion %", "Academic Score", "Attendance %", "Overall Score"];
            const rows = performanceData.studentMetrics.map(s => [
                `"${s.name}"`,
                `"${s.email}"`,
                `"${s.completionRate}%"`,
                `"${s.academicScore}%"`,
                `"${s.attendanceRate}%"`,
                `"${s.overallPerformance}"`
            ]);

            const csvContent = headers.join(",") + "\n" + rows.map(r => r.join(",")).join("\n");

            // Adding UTF-8 BOM for automatic Excel recognition
            const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const filename = `Performance_Report_${new Date().toISOString().split('T')[0]}.csv`;
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
                URL.revokeObjectURL(url);
            }, 500);

            showSuccess('Export Complete', `Metric report "${filename}" has been successfully generated.`);
        } catch (err) {
            console.error("Export Error:", err);
            showError('Export Failure', 'A system error occurred while generating the CSV file.');
        } finally {
            closeLoading();
        }
    };

    const studentColumns = [
        {
            header: 'Candidate Identity', key: 'name', width: '25%', render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="avatar" style={{ width: '32px', height: '32px', fontSize: '12px' }}>{row.name.charAt(0)}</div>
                    <div>
                        <strong style={{ color: '#0F172A', display: 'block' }}>{row.name}</strong>
                        <span style={{ fontSize: '11px', color: '#64748B' }}>{row.email}</span>
                    </div>
                </div>
            )
        },
        {
            header: 'Course Completion', key: 'completionRate', width: '20%', render: (row) => (
                <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                        <span style={{ color: '#64748B' }}>LMS Progress</span>
                        <strong>{row.completionRate}%</strong>
                    </div>
                    <div className="progress-bar-bg" style={{ background: '#F1F5F9', borderRadius: '4px', height: '6px' }}>
                        <div style={{ width: `${row.completionRate}%`, background: 'var(--primary-blue)', height: '100%', borderRadius: '4px' }}></div>
                    </div>
                </div>
            )
        },
        {
            header: 'Academic Score', key: 'academicScore', width: '15%', render: (row) => (
                <div style={{ textAlign: 'center' }}>
                    <strong style={{ color: row.academicScore >= 75 ? '#10B981' : row.academicScore >= 40 ? '#F59E0B' : '#EF4444' }}>
                        {row.academicScore}%
                    </strong>
                </div>
            )
        },
        {
            header: 'Attendance', key: 'attendanceRate', width: '15%', render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <FaUserClock style={{ color: row.attendanceRate >= 80 ? '#10B981' : '#64748B' }} />
                    <span>{row.attendanceRate}%</span>
                </div>
            )
        },
        {
            header: 'Performance Index', key: 'overallPerformance', width: '15%', render: (row) => (
                <span className={`status-badge ${parseFloat(row.overallPerformance) >= 70 ? 'active' : 'pending'}`} style={{ width: '45px', justifyContent: 'center' }}>
                    {row.overallPerformance}
                </span>
            )
        },
        {
            header: 'Profile', key: 'actions', width: '10%', render: (row) => (
                <button className="action-btn-gold sm" title="View Full Candidate Profile" onClick={() => handleViewProfile(row)}>
                    <FaEye />
                </button>
            )
        }
    ];

    if (loading && !performanceData) return <Loader />;

    const avgCompletion = performanceData ? (performanceData.studentMetrics.reduce((sum, s) => sum + parseFloat(s.completionRate), 0) / performanceData.studentMetrics.length || 0).toFixed(1) : 0;
    const avgScore = performanceData ? (performanceData.studentMetrics.reduce((sum, s) => sum + parseFloat(s.academicScore), 0) / performanceData.studentMetrics.length || 0).toFixed(1) : 0;
    const avgAttendance = performanceData ? (performanceData.studentMetrics.reduce((sum, s) => sum + parseFloat(s.attendanceRate), 0) / performanceData.studentMetrics.length || 0).toFixed(1) : 0;

    // Chart Data
    const chartData = performanceData ? performanceData.studentMetrics.map(s => ({
        name: s.name.split(' ')[0],
        overall: parseFloat(s.overallPerformance)
    })).slice(0, 10) : [];

    return (
        <div className="dashboard-overview fade-in">
            {/* Header section with course selector */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="page-title">Performance Command Center</h1>
                    <p className="page-subtitle">Real-time data aggregation of student curriculum progress, test scores, and attendance.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <select
                        className="glass-input"
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                        style={{ minWidth: '200px', height: '44px' }}
                    >
                        {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                    </select>
                    <button className="primary-btn pulse-on-hover" onClick={handleExportCSV}>
                        <FaDownload style={{ marginRight: '8px' }} /> Export Analytics
                    </button>
                </div>
            </div>

            {/* Performance KPI Grid */}
            <div className="kpi-grid" style={{ marginBottom: '32px' }}>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Avg. Completion Rate</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaUserGraduate /></div>
                    </div>
                    <h2 className="kpi-value">{avgCompletion}%</h2>
                    <div className={`kpi-trend ${parseFloat(avgCompletion) > 70 ? 'trend-up' : 'trend-neutral'}`}>
                        {parseFloat(avgCompletion) > 70 ? <FaCheckCircle /> : <FaExclamationTriangle />}
                        <span>{parseFloat(avgCompletion) > 70 ? 'On Track' : 'Needs Push'}</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Batch Academic Score</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}><FaChartLine /></div>
                    </div>
                    <h2 className="kpi-value">{avgScore}%</h2>
                    <div className="kpi-trend trend-up">
                        <FaRegFileAlt /> <span>Verified by Auto-Grader</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Presence Multiplier</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}><FaUserClock /></div>
                    </div>
                    <h2 className="kpi-value">{avgAttendance}%</h2>
                    <div className="kpi-trend trend-neutral">
                        <span>Live Session Attendance</span>
                    </div>
                </div>
            </div>

            {/* Analytics Section */}
            <div className="charts-grid" style={{ marginBottom: '32px', gridTemplateColumns: '1.5fr 1fr' }}>
                <div className="chart-card">
                    <div className="section-title">
                        <span>Top Performers (Performance Index)</span>
                        <FaChartLine style={{ opacity: 0.5 }} />
                    </div>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} domain={[0, 100]} />
                                <Tooltip
                                    contentStyle={{ background: '#0F172A', border: 'none', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                    cursor={{ fill: 'rgba(0, 44, 95, 0.05)' }}
                                />
                                <Bar dataKey="overall" radius={[4, 4, 0, 0]} barSize={40}>
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.overall >= 80 ? '#10B981' : entry.overall >= 60 ? '#3B82F6' : '#EF4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="chart-card">
                    <div className="section-title">
                        <span>Metric Distribution</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0' }}>
                        <div className="distribution-item">
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                                <span>Curriculum Progress</span>
                                <strong>{avgCompletion}%</strong>
                            </div>
                            <div style={{ height: '8px', background: '#F1F5F9', borderRadius: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${avgCompletion}%`, background: '#3B82F6', height: '100%' }}></div>
                            </div>
                        </div>
                        <div className="distribution-item">
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                                <span>Assessment Quality</span>
                                <strong>{avgScore}%</strong>
                            </div>
                            <div style={{ height: '8px', background: '#F1F5F9', borderRadius: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${avgScore}%`, background: '#10B981', height: '100%' }}></div>
                            </div>
                        </div>
                        <div className="distribution-item">
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                                <span>Live Engagement</span>
                                <strong>{avgAttendance}%</strong>
                            </div>
                            <div style={{ height: '8px', background: '#F1F5F9', borderRadius: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${avgAttendance}%`, background: '#F59E0B', height: '100%' }}></div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: '30px', padding: '16px', background: '#F8FAFC', borderRadius: '12px', border: '1px dashed #E2E8F0' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748B', lineHeight: '1.6' }}>
                            <FaExclamationTriangle style={{ marginRight: '6px' }} />
                            Performance Index is weighted: 30% Progress, 50% Academics, 20% Attendance. Data is synced with Candidate DB for recruitment.
                        </p>
                    </div>
                </div>
            </div>

            {/* Detailed Gradebook Table */}
            {performanceData && performanceData.studentMetrics.length > 0 ? (
                <DataTable
                    title="Detailed Performance Roster"
                    columns={studentColumns}
                    data={performanceData.studentMetrics.filter(s =>
                        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        s.email.toLowerCase().includes(searchTerm.toLowerCase())
                    )}
                    actions={
                        <div className="search-wrapper" style={{ width: '250px' }}>
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Filter by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    }
                />
            ) : (
                <EmptyState
                    icon={FaUserGraduate}
                    title="No Performance Data"
                    message="There are no students enrolled or no performance records found for this course yet."
                />
            )}

            {/* HIGHLY STYLED STUDENT PROFILE MODAL */}
            {isProfileModalOpen && createPortal(
                <div className={`modal-overlay ${isProfileModalOpen ? 'open' : ''}`} onClick={closeProfileModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', height: '85vh' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #002C5F, #00458E)', color: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '12px' }}>
                                    <FaUserGraduate size={24} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>Candidate Intelligence Profile</h2>
                                    <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>Comprehensive Academic & Performance Audit</span>
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={closeProfileModal}><FaTimes /></button>
                        </div>

                        <div className="modal-body" style={{ background: '#f8fafc', padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                            {profileLoading ? (
                                <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Loader />
                                </div>
                            ) : profileDetails ? (
                                <div className="view-modal-layout" style={{ display: 'flex', flexWrap: 'nowrap', height: '100%', overflow: 'hidden' }}>
                                    {/* Left Sidebar: Student Identity */}
                                    <div style={{ flex: '0 0 350px', padding: '30px', background: 'white', borderRight: '1px solid #e2e8f0', overflowY: 'auto' }} className="custom-scrollbar">
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <div style={{
                                                width: '120px',
                                                height: '120px',
                                                borderRadius: '50%',
                                                margin: '0 auto 20px',
                                                background: '#002C5F',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '48px',
                                                color: 'white',
                                                overflow: 'hidden',
                                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                            }}>
                                                {profileDetails.profile?.profileImage ? (
                                                    <img src={`${API_URL}${profileDetails.profile.profileImage}`} alt={profileDetails.profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : profileDetails.profile?.name.charAt(0)}
                                            </div>
                                            <h3 style={{ margin: '0 0 8px 0', color: '#0F172A', fontSize: '20px' }}>{profileDetails.profile?.name}</h3>
                                            <span style={{
                                                color: '#3B82F6',
                                                background: '#EFF6FF',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: 700
                                            }}>STUDENT ACCOUNT</span>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748B', fontSize: '14px' }}>
                                                <FaEnvelope style={{ color: '#002C5F' }} /> {profileDetails.profile?.email}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748B', fontSize: '14px' }}>
                                                <FaPhone style={{ color: '#002C5F' }} /> {profileDetails.profile?.phone || 'Not Shared'}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748B', fontSize: '14px' }}>
                                                <FaMapMarkerAlt style={{ color: '#002C5F' }} /> {profileDetails.profile?.location || 'Location Unspecified'}
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '32px' }}>
                                            <h4 className="premium-label" style={{ marginBottom: '16px' }}>ACADEMIC INFO</h4>
                                            <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                                                <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, marginBottom: '4px' }}>HIGHEST EDUCATION</div>
                                                <div style={{ fontSize: '14px', color: '#0F172A', fontWeight: 600 }}>{profileDetails.profile?.academicInfo?.education || 'Bachelors in CS'}</div>
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '24px' }}>
                                            <h4 className="premium-label" style={{ marginBottom: '12px' }}>CORE SKILLS</h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {profileDetails.profile?.academicInfo?.skills?.length > 0 ? (
                                                    profileDetails.profile.academicInfo.skills.map((skill, i) => (
                                                        <span key={i} style={{ background: '#F1F5F9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>{skill}</span>
                                                    ))
                                                ) : ['React', 'Node.js', 'Python', 'Docker'].map((s, i) => (
                                                    <span key={i} style={{ background: '#F1F5F9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Content: Stats & History */}
                                    <div style={{ flex: '1', padding: '30px', overflowY: 'auto' }} className="custom-scrollbar">
                                        <div style={{ marginBottom: '32px' }}>
                                            <h4 className="premium-label" style={{ borderBottom: '2px solid #002C5F', paddingBottom: '6px', display: 'inline-block' }}>COURSE PERFORMANCE MASTERY</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '16px' }}>
                                                {profileDetails.courses?.length > 0 ? profileDetails.courses.map((c, i) => (
                                                    <div key={i} style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                        <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: '12px' }}>{c.title}</div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                            <div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                                                                    <span style={{ color: '#64748B' }}>LMS Progress</span>
                                                                    <strong>{c.progress}%</strong>
                                                                </div>
                                                                <div style={{ height: '6px', background: '#F1F5F9', borderRadius: '4px', overflow: 'hidden' }}>
                                                                    <div style={{ width: `${c.progress}%`, height: '100%', background: '#3B82F6' }}></div>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748B' }}>
                                                                    <FaUserClock /> Presence: <strong>{c.attendance}%</strong>
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '11px',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    background: c.status === 'Completed' ? '#F0FDF4' : '#FFF7ED',
                                                                    color: c.status === 'Completed' ? '#166534' : '#9A3412',
                                                                    fontWeight: 700
                                                                }}>
                                                                    {c.status.toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )) : (
                                                    <div style={{ gridColumn: '1 / -1', padding: '30px', textAlign: 'center', background: '#F8FAFC', borderRadius: '16px', color: '#64748B' }}>
                                                        This student is currently only in the system through this specific course.
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 className="premium-label">RECENT ASSESSMENT SUBMISSIONS</h4>
                                            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                {profileDetails.recentSubmissions?.length > 0 ? profileDetails.recentSubmissions.map((s, i) => (
                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '16px', background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '10px',
                                                            background: s.type === 'Exam' ? '#FEF2F2' : s.type === 'Quiz' ? '#EFF6FF' : '#F0FDF4',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: s.type === 'Exam' ? '#EF4444' : s.type === 'Quiz' ? '#3B82F6' : '#10B981'
                                                        }}>
                                                            {s.type === 'Exam' ? <FaGlobe /> : s.type === 'Quiz' ? <FaHistory /> : <FaTrophy />}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#0F172A' }}>{s.assessmentId?.title}</div>
                                                            <div style={{ fontSize: '12px', color: '#94A3B8' }}>{s.courseId?.title} • {new Date(s.createdAt).toLocaleDateString()}</div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: '15px', fontWeight: 800, color: '#0F172A' }}>{s.obtainedMarks} / {s.totalMarks}</div>
                                                            <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>SCORE</div>
                                                        </div>
                                                    </div>
                                                )) : (
                                                    <div style={{ padding: '20px', textAlign: 'center', color: '#94A3B8', fontStyle: 'italic', fontSize: '14px' }}>
                                                        No recent submissions found for this candidate.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <FaExclamationTriangle size={48} color="#94A3B8" />
                                    <h3 style={{ marginTop: '16px' }}>Data Synchronization Error</h3>
                                    <p>We could not pull the full telemetry for this student. Please try again.</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer" style={{ padding: '20px 30px', borderTop: '1px solid #e2e8f0', background: 'white', display: 'flex', justifyContent: 'flex-end' }}>
                            <button className="primary-btn" onClick={closeProfileModal} style={{ padding: '10px 30px' }}>
                                Close Audit View
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorStudents;
